/**
 * Revenue Breakdown Chart Component
 *
 * Reusable donut chart for displaying revenue breakdown by category
 * (Income Account, Item, or Payment Method). Shows percentages in legend.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import type { CategoryDataPoint } from '../../../types/financialInsights';
import {
  formatChartCurrency,
  getChartColors,
  calculatePercentage,
  truncateLabel,
} from '../../../utils/chartHelpers';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

interface RevenueBreakdownChartProps {
  data: CategoryDataPoint[] | undefined;
  loading: boolean;
  title: string;
}

/**
 * Loading skeleton for chart
 */
const ChartSkeleton: React.FC = () => (
  <Box sx={{ height: 350 }}>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="rectangular" width={200} height={32} />
    </Box>
    <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto' }} />
  </Box>
);

/**
 * Revenue Breakdown Chart
 */
export const RevenueBreakdownChart: React.FC<RevenueBreakdownChartProps> = ({
  data,
  loading,
  title,
}) => {
  const theme = useTheme();

  // Show skeleton during loading
  if (loading) {
    return (
      <Card>
        <CardContent>
          <ChartSkeleton />
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{ height: 350 }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No revenue data found for this category
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calculate total for percentages
  // Parse string values to numbers to avoid NaN
  const totalRevenue = data.reduce(
    (sum, item) => sum + parseFloat(item.value.toString()),
    0
  );

  // Prepare chart data
  const chartData = {
    labels: data.map(item => truncateLabel(item.category, 25)),
    datasets: [
      {
        data: data.map(item => item.value),
        backgroundColor: getChartColors(data.length),
        borderColor: theme.palette.background.paper,
        borderWidth: 2,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          color: theme.palette.text.primary,
          font: {
            family: 'Arial, sans-serif',
            size: 11,
          },
          generateLabels: (chart: any) => {
            const { datasets } = chart.data;
            return chart.data.labels.map((label: string, i: number) => {
              const value = datasets[0].data[i];
              const percentage = calculatePercentage(value, totalRevenue);
              return {
                text: `${label} (${percentage})`,
                fillStyle: datasets[0].backgroundColor[i],
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 4,
        callbacks: {
          label: (context: any) => {
            const item = data[context.dataIndex];
            const percentage = calculatePercentage(item.value, totalRevenue);
            return [
              `${item.category}`,
              `Amount: ${formatChartCurrency(item.value)}`,
              `Percentage: ${percentage}`,
            ];
          },
        },
      },
    },
    cutout: '65%',
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ height: 250, position: 'relative' }}>
          <Doughnut data={chartData} options={chartOptions} />
          {/* Center text showing total */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '25%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatChartCurrency(totalRevenue, 'USD', true)}
            </Typography>
          </Box>
        </Box>

        {/* Summary statistics */}
        <Box mt={2} display="flex" justifyContent="center" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {data.length} {data.length === 1 ? 'category' : 'categories'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RevenueBreakdownChart;
